<template>
  <v-card class="rounded-lg pb-5 mb-4 ap-card" outlined v-if="isFormReady">
    <v-card-title class="pb-1 product-card-title ap-card-title">
      <span class="ap-card-accent"></span>
      <span>{{ $t("product_settings") }}</span>
      <span class="ap-card-subtitle">{{ $t("product_settings_subtitle") }}</span>
    </v-card-title>
    <v-card-text :class="$vuetify.breakpoint.mobile ? '' : 'px-4 py-0'">
      <!-- Currency Selection -->
      <v-card v-if="showCurrency" class="mb-4 rounded-lg product-settings-currency" outlined>
        <v-card-title>{{ $t("currency") }}</v-card-title>
        <v-card-text>
          <v-chip-group
            v-model="currentCurrencyId"
            column
          >
            <v-chip
              v-for="(currency, index) in currencies"
              :key="index"
              :value="currency.id"
              class="rounded-lg"
            >
              <v-avatar size="15" class="mr-2">
                <v-img :src="currency.flag" />
              </v-avatar>
              {{ currency.code }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
      </v-card>

      <!-- Toppings -->
      <v-card class="mb-4 rounded-lg" outlined>
        <div class="d-flex align-center pa-4 pb-2">
          <span class="font-weight-bold text-subtitle-1">
            {{ $t("toppings") || "ທັອບປິ້ງ" }}
          </span>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            depressed
            class="rounded-lg px-3"
            @click="showToppingsModal = true"
          >
            <v-icon left size="14">fal fa-pen</v-icon>
            {{ $t("add_topping") || "ເພີ່ມທັອບປິ້ງ" }}
          </v-btn>
        </div>

        <v-card-text class="pt-0 pb-4">
          <div v-if="!selectedToppings.length" class="text-center py-4 grey--text text-body-2">
            <v-icon color="grey lighten-1" size="32" class="mb-2 display-block">fal fa-layer-group</v-icon>
            <div>{{ $t("no_topping_selected") || "ບໍ່ມີທັອບປິ້ງທີ່ເລືອກ" }}</div>
            <v-btn
              small
              outlined
              color="primary"
              class="mt-2 rounded-lg"
              @click="showToppingsModal = true"
            >
              <v-icon left size="14">fal fa-plus</v-icon>
              {{ $t("add_topping") || "ເພີ່ມທັອບປິ້ງ" }}
            </v-btn>
          </div>
          <div v-else class="d-flex flex-wrap pt-2">
            <v-chip
              v-for="topping in selectedToppings"
              :key="topping.id"
              close
              color="primary"
              outlined
              class="ma-1 font-weight-medium rounded-lg"
              @click:close="toggleTopping(topping.id)"
            >
              <v-avatar left size="20" v-if="getToppingImage(topping.img)">
                <v-img :src="getToppingImage(topping.img)" />
              </v-avatar>
              <span>{{ toppingName(topping) }}</span>
              <span class="ml-2 font-weight-bold primary--text">
                (+{{ formatNumber(topping.price) }})
              </span>
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <!-- Topping Selection Modal -->
      <v-dialog v-model="showToppingsModal" max-width="650px" scrollable>
        <v-card class="rounded-xl">
          <v-card-title class="d-flex align-center pb-3 pt-4 px-5">
            <span class="font-weight-bold text-h6">
              <v-icon left color="primary">fal fa-layer-group</v-icon>
              {{ $t("add_topping") || "ເພີ່ມທັອບປິ້ງ" }}
            </span>
            <v-spacer></v-spacer>
            <v-btn icon small @click="showToppingsModal = false">
              <v-icon>fal fa-times</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pt-4 px-5" style="max-height: 460px;">
            <v-text-field
              v-model="toppingSearch"
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="fal fa-search"
              :placeholder="$t('search') || 'ຄົ້ນຫາ...'"
              class="mb-3 rounded-lg"
            />

            <div class="d-flex align-center justify-space-between mb-3">
              <span class="grey--text text-caption font-weight-medium">
                {{ selectedToppings.length }} / {{ toppings.length }} {{ $t("selected") || "ເລືອກແລ້ວ" }}
              </span>
              <div>
                <v-btn text x-small color="primary" class="px-2" @click="selectAllToppings">
                  {{ $t("select_all") || "ເລືອກທັງໝົດ" }}
                </v-btn>
                <v-btn text x-small color="error" class="px-2" @click="clearToppingsSelection">
                  {{ $t("clear_all") || "ລ້າງທັງໝົດ" }}
                </v-btn>
              </div>
            </div>

            <div v-if="!toppings.length" class="text-center py-6 grey--text">
              {{ $t("no_data") || "ບໍ່ມີຂໍ້ມູນ" }}
            </div>

            <div v-else-if="!filteredToppings.length" class="text-center py-6 grey--text">
              {{ $t("no_results_found") || "ບໍ່ພົບຂໍ້ມູນ" }}
            </div>

            <v-row v-else dense>
              <v-col
                v-for="topping in filteredToppings"
                :key="topping.id"
                cols="12"
                sm="6"
              >
                <v-card
                  outlined
                  class="rounded-lg pa-3 cursor-pointer transition-all border-hover"
                  :class="isToppingSelected(topping.id) ? 'blue lighten-5 border-primary-active' : ''"
                  @click="toggleTopping(topping.id)"
                >
                  <div class="d-flex align-center">
                    <v-checkbox
                      :input-value="isToppingSelected(topping.id)"
                      hide-details
                      class="my-0 py-0 mr-2"
                      readonly
                      @click.stop="toggleTopping(topping.id)"
                    />
                    <v-avatar size="36" color="grey lighten-3" class="mr-2 rounded-lg">
                      <v-img
                        :src="getToppingImage(topping.img) || '/images/no_photo.svg'"
                        contain
                      />
                    </v-avatar>
                    <div class="text-truncate flex-grow-1">
                      <div class="font-weight-medium text-body-2 text-truncate">
                        {{ toppingName(topping) }}
                      </div>
                      <div class="primary--text text-caption font-weight-bold">
                        +{{ formatNumber(topping.price) }}
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn
              depressed
              class="rounded-lg px-4"
              @click="showToppingsModal = false"
            >
              {{ $t("cancel") || "ຍົກເລີກ" }}
            </v-btn>
            <v-btn
              color="primary"
              depressed
              class="rounded-lg px-4"
              @click="showToppingsModal = false"
            >
              <v-icon left size="16">fal fa-check</v-icon>
              {{ $t("confirm") || "ຕົກລົງ" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Product Expired -->
      <v-card class="mb-4 rounded-lg" outlined>
        <v-card-text>
          <!-- Show normal expiration controls when not a bundle -->
          <template v-if="!form.is_bundle">
            <div
              class="d-flex align-center justify-space-between"
              style="gap: 12px"
            >
              <div style="min-width: 0">
                <h6>
                  {{ $t("expiry_settings") }}
                </h6>
                <p class="mb-0 text-caption grey--text">
                  {{ $t("expiry_settings_desc") }}
                </p>
              </div>
              <v-switch
                class="expired-check ma-0 pa-0 flex-grow-0 flex-shrink-0"
                v-model="localExpiredPro"
                inset
                hide-details
              />
            </div>
            <template v-if="localExpiredPro">
              <DatePicker
                label="expired_date"
                v-model="form.expired_date"
                :readonly="false"
              />
            </template>
          </template>

          <!-- Show disabled expiration controls when bundle is enabled -->
          <template v-if="form.is_bundle">
            <div
              class="d-flex align-center justify-space-between"
              style="gap: 12px"
            >
              <div style="min-width: 0">
                <h6>
                  {{ $t("expiry_settings") }}
                </h6>
                <p class="mb-0 text-caption grey--text">
                  {{ $t("expiry_settings_desc") }}
                </p>
              </div>
              <v-switch
                class="expired-check ma-0 pa-0 flex-grow-0 flex-shrink-0"
                v-model="localExpiredPro"
                inset
                hide-details
                disabled
              />
            </div>
            <template v-if="localExpiredPro">
              <DatePicker
                label="expired_date"
                v-model="form.expired_date"
                disabled
              />
            </template>
          </template>
        </v-card-text>
      </v-card>

      <!-- Stock Tracking -->
      <v-card v-if="showStock" class="mb-4 rounded-lg product-settings-stock" outlined>
        <v-card-title>{{ $t("inventory") }}</v-card-title>
        <v-card-text class="pb-4">
          <v-switch
            v-model="form.track_stock"
            :label="$t('track_stock')"
            class="my-0 py-0"
            inset
            hide-details
            :disabled="form.is_bundle"
          />

          <!-- Show editable stock fields when track_stock is enabled, no bundles, and no variants -->
          <template v-if="form.track_stock && !form.is_bundle">
            <v-row>
              <v-col xs="12" sm="12" md="6" lg="6" xl="6">
                <CurrencyInput
                  :disabled="!edit_stock || form.has_variations"
                  label="stock"
                  v-model="form.stock"
                  :options="options"
                />
              </v-col>
              <v-col xs="12" sm="12" md="6" lg="6" xl="6">
                <CurrencyInput
                  typeShow="number"
                  :options="options"
                  :errorMessages="notify_stockError"
                  label="stock_notify"
                  v-model="form.notify_stock"
                />
              </v-col>
            </v-row>
          </template>

          <!-- Show stock fields when bundles exist: stock disabled, notify_stock always editable -->
          <template v-if="form.is_bundle && form.track_stock">
            <v-row>
              <v-col xs="12" sm="12" md="6" lg="6" xl="6">
                <CurrencyInput
                  :disabled="true"
                  :label="$t('stock_disabled_label') || 'stock (disabled)'"
                  v-model="form.stock"
                  :hint="
                    $t('stock_disabled_hint') ||
                    'Stock is managed at bundle level'
                  "
                  persistent-hint
                />
              </v-col>
              <v-col xs="12" sm="12" md="6" lg="6" xl="6">
                <CurrencyInput
                  typeShow="number"
                  :options="options"
                  :errorMessages="notify_stockError"
                  label="stock_notify"
                  v-model="form.notify_stock"
                  :hint="
                    $t('stock_notify_hint') ||
                    'Stock notification threshold for bundle products'
                  "
                  persistent-hint
                />
              </v-col>
            </v-row>
          </template>

          <!-- Show message when variants are enabled -->
          <template
            v-if="form.variants && form.variants.length > 0 && !form.is_bundle"
          >
            <v-alert type="info" dense outlined class="mt-2">
              {{
                $t("stock_managed_at_variant_level") ||
                (edit_stock
                  ? "Stock is managed at the variant level. Individual variant stock levels can be edited. Product-level stock fields are disabled."
                  : "Stock can be tracked at the variant level. Enable 'Track Stock' to manage individual variant stock levels.")
              }}
            </v-alert>
          </template>

          <!-- Show message when track_stock is disabled due to variants/bundles -->
          <template v-if="!form.track_stock && form.is_bundle">
            <v-alert type="warning" dense outlined class="mt-2">
              {{
                $t("stock_tracking_disabled_for_variants_bundles") ||
                "Stock tracking is automatically disabled for products with variants or bundles"
              }}
            </v-alert>
          </template>
        </v-card-text>
      </v-card>

      <!-- Product Variants -->
      <v-card class="mb-4 rounded-lg" outlined>
        <v-card-text class="pb-0">
          <div
            class="d-flex align-center justify-space-between"
            style="gap: 12px"
          >
            <div style="min-width: 0">
              <h6>
                {{ $t("variant_options") }}
              </h6>
              <p class="mb-0 text-caption grey--text">
                {{ $t("variant_options_desc") }}
              </p>
            </div>
            <v-switch
              v-model="variationsEnabled"
              class="ma-0 pa-0 flex-grow-0 flex-shrink-0"
              inset
              hide-details
            />
          </div>
          <div v-if="variationsEnabled" class="mt-3">
            <TextSelect
              v-model="variantTypeSelect"
              :items="variantTypeOptions"
              :label="$t('variant_type')"
              text="label"
              outlined
              dense
              hide-details
              class="mb-3"
              :disabled="form.is_bundle"
            />
            <ProductVariations
              v-if="shouldShowVariants"
              :key="`variations-${variationsEnabled}`"
              ref="productVariations"
              :product-id="productId"
              :initial-options="form.options || []"
              :initial-variants="form.variants || []"
              @variations-updated="handleVariationsUpdated"
              :showOptions="true"
              :parent-product="form"
              :trackStock="form.track_stock"
              :editStock="edit_stock"
              :disabled="form.is_bundle"
              :recipe-mode="!!form.has_bom"
              :ingredients="ingredients"
              @variant-recipes-updated="handleVariantRecipesUpdated"
            />

            <!-- Show message when variants are disabled due to bundle -->
            <template v-if="form.is_bundle && variationsEnabled">
              <v-alert type="warning" dense outlined class="mt-2">
                {{
                  $t("variants_disabled_for_bundle") ||
                  "Variant functionality is disabled when bundle is enabled. These features are mutually exclusive."
                }}
              </v-alert>
            </template>
          </div>
        </v-card-text>
      </v-card>

      <!-- Product Bundle -->
      <ProductBundleBuilder
        :isBundle.sync="form.is_bundle"
        :items.sync="localBundleItems"
        :disabled="(form.variants && form.variants.length > 0) || hasVariations"
      />

      <!-- Show message when bundle is disabled due to variants -->
      <template
        v-if="
          ((form.variants && form.variants.length > 0) || hasVariations) &&
          form.is_bundle
        "
      >
        <v-alert type="warning" dense outlined class="mt-2">
          {{
            $t("bundle_disabled_for_variants") ||
            "Bundle functionality is disabled when variants are enabled. These features are mutually exclusive."
          }}
        </v-alert>
      </template>

      <!-- Dynamic Pricing -->
      <v-card class="mb-4 rounded-lg" outlined>
        <v-card-text>
          <div
            class="d-flex align-center justify-space-between"
            style="gap: 12px"
          >
            <div>
              <h6>
                {{ $t("pricing_settings") || "Pricing Settings" }}
              </h6>
              <p class="mb-0 text-caption grey--text">
                {{ $t("pricing_settings_desc") }}
              </p>
            </div>
            <v-switch
              v-model="form.dynamic_pricing"
              class="ma-0 pa-0 flex-grow-0 flex-shrink-0"
              inset
              hide-details
              :disabled="
                hasVariations || (form.variants && form.variants.length > 0)
              "
            />
          </div>
          <v-alert
            v-if="form.dynamic_pricing"
            type="info"
            dense
            outlined
            class="mt-2"
          >
            {{
              $t("dynamic_pricing_info") ||
              "When enabled, cashiers can enter custom prices when selling this product."
            }}
          </v-alert>
          <v-alert
            v-if="
              (hasVariations || (form.variants && form.variants.length > 0)) &&
              form.dynamic_pricing
            "
            type="warning"
            dense
            outlined
            class="mt-2"
          >
            {{
              $t("dynamic_pricing_disabled_for_variants") ||
              "Dynamic pricing is disabled for products with variations. These features are mutually exclusive."
            }}
          </v-alert>
        </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
import TTextField from "@/components/global/TextField";
import CurrencyInput from "@/components/CurrencyInput";
import DatePicker from "@/components/global/DatePicker";
import ProductVariations from "@/components/products/ProductVariations";
import ProductBundleBuilder from "@/components/products/ProductBundleBuilder.vue";
import TextSelect from "@/components/global/TextSelect";

export default {
  name: "ProductSettings",
  components: {
    TTextField,
    CurrencyInput,
    DatePicker,
    ProductVariations,
    ProductBundleBuilder,
    TextSelect,
  },
  data() {
    return {
      internalBundleItems: [],
      localExpiredPro: false, // Local copy of expiredPro
      ingredients: [],
      toppings: [],
      showToppingsModal: false,
      toppingSearch: "",
    };
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    currencies: {
      type: Array,
      default: () => [],
    },
    expiredPro: {
      type: Boolean,
      default: false,
    },
    hasVariations: {
      type: Boolean,
      default: false,
    },
    bundleItems: {
      type: Array,
      default: () => [],
    },
    productId: {
      type: [String, Number],
      default: null,
    },
    edit_stock: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    notify_stockError: {
      type: String,
      default: "",
    },
    showCurrency: {
      type: Boolean,
      default: false,
    },
    showStock: {
      type: Boolean,
      default: false,
    },
    // For edit-product.vue compatibility
    showVariants: {
      type: Boolean,
      default: null,
    },
  },
  emits: [
    "update:expiredPro",
    "update:hasVariations",
    "update:showVariants",
    "update:bundleItems",
    "variations-updated",
  ],
  computed: {
    variantTypeOptions() {
      return [
        { value: "standard", label: this.$t("standard_variants") || "Standard Variants" },
        { value: "recipe", label: this.$t("recipe_variants") || "Recipe Variants" },
      ];
    },
    variantTypeSelect: {
      get() {
        const val = this.form.has_bom ? "recipe" : "standard";
        return this.variantTypeOptions.find(o => o.value === val) || this.variantTypeOptions[0];
      },
      set(option) {
        const isRecipe = option && option.value === "recipe";
        this.$set(this.form, "has_bom", isRecipe);
        this.$set(this.form, "is_bundle", false);
        this.$set(this.form, "dynamic_pricing", false);
      },
    },
    // Check if form is properly initialized
    isFormReady() {
      return this.form && typeof this.form === "object";
    },
    // Check if we should show variants section
    shouldShowVariants() {
      return this.variationsEnabled && this.form;
    },
    // Computed property for currency_id to ensure reactivity
    currentCurrencyId: {
      get() {
        const currencyId = this.form?.currency_id;
        return currencyId;
      },
      set(value) {
        if (this.form) {
          this.$set(this.form, "currency_id", value);
        }
      },
    },
    variationsEnabled: {
      get() {
        // Use showVariants if provided (for edit-product), otherwise use hasVariations
        return this.showVariants !== null
          ? this.showVariants
          : this.hasVariations;
      },
      set(value) {
        // Emit the appropriate event based on which prop is being used
        if (this.showVariants !== null) {
          this.$emit("update:showVariants", value);
        } else {
          this.$emit("update:hasVariations", value);
        }
        if (!value) {
          this.$set(this.form, "has_bom", false);
        }
      },
    },
    localBundleItems: {
      get() {
        return this.internalBundleItems;
      },
      set(value) {
        this.internalBundleItems = value;
        this.$emit("update:bundleItems", value);
      },
    },
    selectedToppings() {
      const ids = this.form ? (this.form.topping_ids || []) : [];
      const master = this.toppings || [];
      const original = (this.form && this.form.toppings) || [];
      return ids
        .map(
          (id) =>
            master.find((t) => t.id === id) ||
            original.find((t) => t.id === id)
        )
        .filter(Boolean);
    },
    filteredToppings() {
      if (!this.toppingSearch) return this.toppings || [];
      const search = this.toppingSearch.toLowerCase();
      return (this.toppings || []).filter((t) => {
        const nameLa = (t.name_la || "").toLowerCase();
        const nameEn = (t.name_en || "").toLowerCase();
        const nameTh = (t.name_th || "").toLowerCase();
        const nameCn = (t.name_cn || "").toLowerCase();
        return (
          nameLa.includes(search) ||
          nameEn.includes(search) ||
          nameTh.includes(search) ||
          nameCn.includes(search)
        );
      });
    },
  },
  methods: {
    handleVariationsUpdated(variations) {
      // Set hasVariations to true when variants are created
      if (variations && variations.length > 0) {
        this.$emit("update:hasVariations", true);
      }

      this.$emit("variations-updated", variations);
    },
    handleVariantRecipesUpdated(recipes) {
      this.$set(this.form, "variant_recipes", recipes || {});
    },
    async fetchIngredients() {
      try {
        const { data } = await this.$http.get("ingredients?include_usage_conversions=1");
        if (data.status) this.ingredients = data.data || [];
      } catch (error) {
        console.log("Error fetching ingredients:", error);
      }
    },
    async fetchToppings() {
      try {
        const { data } = await this.$http.get("toppings?is_active=1");
        if (data.status) {
          this.toppings = data.data || [];
        }
      } catch (error) {
        console.log("Error fetching toppings:", error);
      }
    },
    getToppingImage(img) {
      if (!img || typeof img !== "string") return null;
      if (
        img.startsWith("http://") ||
        img.startsWith("https://") ||
        img.startsWith("data:")
      ) {
        return img;
      }
      const baseURL = process.env.VUE_APP_BASE_URL || "";
      if (img.startsWith("/uploads/")) {
        return `${baseURL}${img}`;
      }
      return `${baseURL}/uploads/${img}`;
    },
    toppingName(topping) {
      if (!topping) return "";
      const lang = this.$i18n ? this.$i18n.locale : "la";
      if (lang === "en" && topping.name_en) return topping.name_en;
      if (lang === "th" && topping.name_th) return topping.name_th;
      if ((lang === "ch" || lang === "cn") && topping.name_cn) return topping.name_cn;
      return topping.name_la || topping.name_en || topping.name_th || topping.name_cn || "";
    },
    formatNumber(val) {
      if (val === null || val === undefined) return "0";
      return Number(val).toLocaleString();
    },
    selectAllToppings() {
      const allIds = (this.filteredToppings || []).map((t) => t.id);
      const current = this.form.topping_ids || [];
      const merged = Array.from(new Set([...current, ...allIds]));
      this.$set(this.form, "topping_ids", merged);
    },
    clearToppingsSelection() {
      this.$set(this.form, "topping_ids", []);
    },
    isToppingSelected(id) {
      return (this.form.topping_ids || []).includes(id);
    },
    toggleTopping(id) {
      const current = this.form.topping_ids || [];
      const next = current.includes(id)
        ? current.filter((toppingId) => toppingId !== id)
        : [...current, id];
      this.$set(this.form, "topping_ids", next);
    },

    // Method to get current variants and options data
    getCurrentVariationsData() {
      if (this.$refs.productVariations) {
        const variants = this.$refs.productVariations.variants || [];
        const options = this.$refs.productVariations.options || [];

        return { variants, options };
      }
      return { variants: [], options: [] };
    },

    // Force update method for currency changes
    forceCurrencyUpdate() {
      this.$forceUpdate();
    },
    // Initialize component when form data changes
    initializeComponent() {
      if (this.form && this.variationsEnabled) {
        // Ensure the component is properly initialized
        this.$nextTick(() => {
          if (this.shouldShowVariants) {
            this.$forceUpdate();
          }
        });
      }
    },
  },
  mounted() {
    // Initialize component if form data is already available
    this.$nextTick(() => {
      this.initializeComponent();
    });
    this.fetchIngredients();
    this.fetchToppings();
  },
  watch: {
    // Watch for changes in the expiredPro prop from parent
    expiredPro: {
      immediate: true,
      handler(newVal) {
        this.localExpiredPro = newVal;
      },
    },
    // Watch for form data changes to ensure proper initialization
    form: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          // Initialize component when form is available
          this.initializeComponent();
        }

        // Force update when form changes
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      },
    },
    // Watch for changes in localExpiredPro and emit to parent
    localExpiredPro(newVal) {
      this.$emit("update:expiredPro", newVal);
    },
    // Watch for currency_id changes to ensure UI updates
    "form.currency_id": {
      immediate: true,
      handler(newVal, oldVal) {
        // Force update to ensure UI reflects the change
        this.$nextTick(() => {
          this.forceCurrencyUpdate();
        });
      },
    },
    hasVariations(newVal) {
      this.$emit("update:hasVariations", newVal);

      // Update form.has_variations when hasVariations changes
      if (this.form) {
        this.form.has_variations = newVal;
      }

      // Note: track_stock can remain enabled for variants - stock will be managed at variant level
    },
    showVariants(newVal) {
      this.$emit("update:showVariants", newVal);

      // Note: track_stock can remain enabled for variants - stock will be managed at variant level
    },
    bundleItems: {
      immediate: true,
      handler(newVal) {
        this.internalBundleItems = Array.isArray(newVal) ? [...newVal] : [];

        // Automatically disable track_stock when bundle is enabled
        if (
          this.internalBundleItems.length > 0 &&
          this.isFormReady &&
          this.form.track_stock
        ) {
          this.form.track_stock = false;
        }
      },
    },

    // Watch for bundle changes to automatically disable expiration
    "form.is_bundle": {
      immediate: true,
      handler(newVal) {
        if (newVal && this.isFormReady) {
          // When bundle is enabled, disable expiration functionality
          this.localExpiredPro = false;
          this.form.expired_date = null;
        }
      },
    },

    // Watch for variants specifically
    "form.variants": {
      handler(newVal, oldVal) {
        // Initialize component when variants change (including when they become empty)
        this.initializeComponent();

        // Force update when variants change
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      },
      deep: true,
    },
    // Watch for shouldShowVariants changes
    shouldShowVariants: {
      handler(newVal, oldVal) {
        if (newVal && !oldVal) {
          // Component should now be visible, force update
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      },
    },
    // Watch for variationsEnabled changes
    variationsEnabled: {
      handler(newVal, oldVal) {
        // Note: track_stock can remain enabled for variants - stock will be managed at variant level

        // Force update when variations are enabled
        if (newVal && !oldVal) {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      },
    },
  },
};
</script>

<style scoped></style>
